import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Upload, FileText, ScrollText, Wallet, Star, CircleDollarSign, Sparkles, Award, Users, Zap, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { USPS, STEPS, BOETE_TYPES, STATS, TRUSTPILOT_REVIEWS, TRUSTPILOT_SCORE, BLOG_POSTS } from '../mock';

const StepIcon = ({ n }) => {
  const icons = [Upload, FileText, ScrollText, Wallet];
  const Icon = icons[n - 1];
  return (
    <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
      <Icon className="w-7 h-7 text-[#1e1b4b]" strokeWidth={1.8} />
    </div>
  );
};

const USP_ICONS = [Award, ShieldCheck, Users, Zap];

const TrustpilotStars = ({ rating }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className={`w-5 h-5 flex items-center justify-center ${i < rating ? 'bg-[#00b67a]' : 'bg-slate-200'}`}>
        <Star className="w-3.5 h-3.5 text-white fill-white" strokeWidth={0} />
      </div>
    ))}
  </div>
);

const Home = () => {
  return (
    <div>
      {/* HERO */}
      <section className="hero-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(242,107,31,0.4) 0, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.15) 0, transparent 40%)' }} />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight tracking-tight uppercase">
              Onterechte boete?<br />
              <span className="text-[#f26b1f]">Bezwaarmeesters</span> helpen je!
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mt-6 max-w-2xl leading-relaxed">
              Upload je boete of beschikking en onze juristen dienen kosteloos bezwaar in. No cure, no pay - winnen we? Jij krijgt het geld terug.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/indienen">
                <Button size="lg" className="bg-[#f26b1f] hover:bg-[#d4581a] text-white h-12 px-8 text-base uppercase font-semibold tracking-wide">
                  Gratis indienen <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="mt-8 inline-flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2 border border-white/20">
              <div className="flex gap-0.5">
                {Array.from({length: 4}).map((_, i) => <div key={i} className="w-4 h-4 bg-[#00b67a] flex items-center justify-center"><Star className="w-2.5 h-2.5 text-white fill-white" strokeWidth={0} /></div>)}
                <div className="w-4 h-4 bg-[#00b67a] flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-slate-300" style={{clipPath:'polygon(20% 0, 100% 0, 100% 100%, 20% 100%)'}} />
                  <Star className="w-2.5 h-2.5 text-white fill-white relative" strokeWidth={0} />
                </div>
              </div>
              <span className="text-sm"><strong>{TRUSTPILOT_SCORE.rating}</strong> op Trustpilot</span>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e1b4b] uppercase tracking-tight">Bezwaar maken in 4 simpele stappen</h2>
            <div className="w-24 h-0.5 bg-[#f26b1f] mx-auto mt-4" />
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {STEPS.map(s => (
              <div key={s.n} className="text-center">
                <div className="flex justify-center mb-4"><StepIcon n={s.n} /></div>
                <div className="font-bold text-[#1e1b4b] uppercase text-sm tracking-wide">{s.n}. {s.title}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/indienen">
              <Button size="lg" className="bg-[#f26b1f] hover:bg-[#d4581a] text-white h-12 px-8 uppercase font-semibold">Gratis indienen <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Kosten */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="w-14 h-14 rounded-2xl bg-[#f26b1f]/10 flex items-center justify-center mb-5">
              <CircleDollarSign className="w-7 h-7 text-[#f26b1f]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e1b4b] uppercase">Zijn er kosten aan verbonden?</h2>
            <p className="text-lg text-slate-700 mt-4 font-semibold">Nee, wij regelen je bezwaar helemaal gratis.</p>
            <div className="mt-5 space-y-3 text-slate-700 leading-relaxed">
              <p>Winnen wij jouw zaak, dan betaalt de <strong>overheid onze vergoeding</strong> voor juridische bijstand.</p>
              <p>Winnen we niet, dan kost het jou <strong>helemaal niets</strong>.</p>
              <p>Daarom is bezwaar maken via Bezwaarmeesters.nl voor jou <strong>altijd volledig kosteloos</strong>.</p>
            </div>
            <div className="mt-8">
              <Link to="/indienen">
                <Button size="lg" className="bg-[#1e1b4b] hover:bg-[#2a2464] text-white h-12 px-7 uppercase font-semibold">Boete indienen <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </Link>
            </div>
          </div>
          <Card className="order-1 lg:order-2 p-8 md:p-10 bg-[#1e1b4b] text-white border-0 rounded-2xl relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#f26b1f]/20 rounded-full blur-3xl" />
            <div className="text-xs uppercase tracking-widest text-[#f26b1f] font-semibold relative">Wist je dat?</div>
            <div className="text-3xl md:text-4xl font-bold mt-3 leading-tight relative">Er wekelijks honderden mensen geld laten liggen door onterechte boetes?</div>
            <div className="mt-6 grid grid-cols-2 gap-4 relative">
              {[['€ 82,30','Parkeerboete'],['€ 240','Snelheidsboete'],['€ 310','Door rood'],['€ 470+','Strafbeschikking']].map(([v,l]) => (
                <div key={l} className="bg-white/5 rounded-lg p-3 border border-white/10">
                  <div className="text-[#f26b1f] font-bold text-lg">{v}</div>
                  <div className="text-xs text-slate-300">{l}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      {/* Waarom Bezwaarmeesters */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e1b4b] uppercase tracking-tight">Waarom kiezen voor Bezwaarmeesters?</h2>
            <div className="w-24 h-0.5 bg-[#f26b1f] mx-auto mt-4" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {USPS.map((usp, i) => {
              const Icon = USP_ICONS[i];
              return (
                <Card key={usp.title} className="p-7 border border-slate-200 card-hover">
                  <div className="w-12 h-12 rounded-xl bg-[#1e1b4b] flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#f26b1f]" />
                  </div>
                  <div className="font-bold text-[#1e1b4b] uppercase text-sm tracking-wide">{usp.title}</div>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{usp.desc}</p>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <div className="italic text-2xl text-[#1e1b4b] font-serif">“Elk bezwaar een meesterzet.”</div>
          </div>
        </div>
      </section>

      {/* Boete types */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e1b4b] uppercase tracking-tight">Voor welke boetes helpen wij?</h2>
            <div className="w-24 h-0.5 bg-[#f26b1f] mx-auto mt-4" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {BOETE_TYPES.map(t => (
              <Card key={t.key} className="card-hover p-6 border border-slate-200 bg-white">
                <div className="w-10 h-10 rounded-md bg-[#f26b1f]/10 text-[#f26b1f] flex items-center justify-center mb-4">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="font-bold text-[#1e1b4b] leading-tight">{t.title}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="py-14 md:py-16 bg-[#1e1b4b] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map(s => (
            <div key={s.label}>
              <div className="text-3xl md:text-5xl font-bold text-[#f26b1f]">{s.value}</div>
              <div className="text-sm md:text-base text-slate-300 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trustpilot reviews */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-2xl font-bold text-[#1e1b4b]">Trustpilot</div>
            <div className="flex justify-center gap-0.5 mt-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={`w-8 h-8 flex items-center justify-center ${i < Math.floor(TRUSTPILOT_SCORE.rating) ? 'bg-[#00b67a]' : i < TRUSTPILOT_SCORE.rating ? 'bg-[#00b67a]/50' : 'bg-slate-200'}`}>
                  <Star className="w-5 h-5 text-white fill-white" strokeWidth={0} />
                </div>
              ))}
            </div>
            <p className="text-slate-600 text-sm mt-3">Cliënten beoordelen ons met <strong>{TRUSTPILOT_SCORE.rating} sterren!</strong></p>
            <a href="https://nl.trustpilot.com/review/bezwaarmeesters.nl" target="_blank" rel="noreferrer" className="text-[#f26b1f] text-sm font-semibold mt-2 inline-block hover:underline">Bekijk alle reviews →</a>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TRUSTPILOT_REVIEWS.map((r, i) => (
              <Card key={i} className="p-5 border border-slate-200 card-hover">
                <TrustpilotStars rating={r.rating} />
                <div className="font-bold text-[#1e1b4b] mt-3">{r.title}</div>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed min-h-[3rem]">{r.text}</p>
                <div className="text-xs text-slate-500 mt-4 pt-3 border-t border-slate-100"><strong className="text-slate-700">{r.name}</strong> • {r.date}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Blog preview */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e1b4b] uppercase">Kennisbank</h2>
              <div className="w-16 h-0.5 bg-[#f26b1f] mt-3" />
            </div>
            <Link to="/blog" className="text-[#1e1b4b] font-semibold hover:text-[#f26b1f] hidden md:flex items-center gap-1">Alle artikelen <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {BLOG_POSTS.slice(0, 3).map(p => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="card-hover">
                <Card className="overflow-hidden border border-slate-200 h-full bg-white">
                  <div className="h-44 bg-slate-200 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <Badge className="bg-[#f26b1f]/10 text-[#f26b1f] border-0 hover:bg-[#f26b1f]/15">{p.category}</Badge>
                    <div className="font-bold text-[#1e1b4b] mt-3 leading-snug">{p.title}</div>
                    <div className="text-sm text-slate-600 mt-2 line-clamp-2">{p.excerpt}</div>
                    <div className="text-xs text-slate-400 mt-3">{p.date} • {p.readTime}</div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="bg-[#1e1b4b] rounded-2xl p-8 md:p-14 text-white text-center relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#f26b1f]/20 rounded-full blur-3xl" />
            <h2 className="text-3xl md:text-4xl font-bold relative uppercase">Klaar om je boete aan te vechten?</h2>
            <p className="text-slate-300 mt-3 max-w-xl mx-auto relative">Binnen 1 minuut geüpload en ontzorgd.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 relative">
              <Link to="/indienen">
                <Button size="lg" className="bg-[#f26b1f] hover:bg-[#d4581a] text-white h-12 px-8 uppercase font-semibold">Gratis indienen <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </Link>
              <Link to="/faq">
                <Button size="lg" variant="outline" className="h-12 px-8 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">Bekijk FAQ</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
